import React, { useState, useEffect, useContext } from "react";
import "./GuestDetailsModal.css";
import DeclinedApprovedModal from "../declinedApprovedModal/DeclinedApprovedModal";
import { formatDateLessThanOneDay } from "../../helper/utils";
import { formatPrice } from "../../helper/utils";
import { Ticket } from "lucide-react";
import { AuthContext } from "../../context/auth";

const GuestDetailsModal = ({
  isOpen = false,
  onClose = () => {},
  guestDetails = {},
  refundDetails = {},
  paymentDetails = {},
  onDecline = async () => {},
  onApprove = async () => {},
  onCancelRefund = async () => {},
  hideRefundActions = false, //true= guest, false= host
  showRSVP = true,
  userRole = [],
  isHistoryTab = false,
}) => {
  const { user } = useContext(AuthContext);

  const activeRole = user?.activeRole;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isUsher = userRole?.includes("usher");

  const isGuestViewing = activeRole === "guest";
  const isHostViewing = activeRole === "host";

  const refundItems = guestDetails?.refundDetails?.eventAttire ?? [];

  const paymentItems = paymentDetails?.eventAttire ?? [];

  const formatAddress = (text, max = 20) => {
    if (!text) return "";
    const formatted = text.charAt(0).toUpperCase() + text.slice(1);
    return formatted.length > max ? formatted.slice(0, max) + "..." : formatted;
  };

  const refundQuantity = refundItems.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0,
  );

  const totalRefundAmount = refundItems.reduce(
    (sum, item) => sum + (item.amountToRefund || 0),
    0,
  );

  const totalAmount = paymentItems.reduce(
    (sum, item) => sum + (item.totalAmount || 0),
    0,
  );
  const currency = paymentDetails.currency || refundDetails.currency || "";

  const refundItemsData = refundItems.map((item) => ({
    ...item,
    unitPriceFormatted: formatPrice(item.price, currency),
    totalPriceFormatted: formatPrice(item.amountToRefund, currency),
  }));

  const paymentItemsData = paymentItems.map((item) => ({
    ...item,
    unitPriceFormatted: formatPrice(item.price, currency),
    totalPriceFormatted: formatPrice(item.totalAmount, currency),
  }));

  const totalPaymentFormatted = formatPrice(totalAmount, currency);

  const totalRefundFormatted = formatPrice(totalRefundAmount, currency);

  const refundStatus = guestDetails?.refundDetails?.refundStatus;


  const cashGiftDescription = paymentDetails.cashGiftDescription ?? "";
  const cashGiftAmountPaid = paymentDetails.cashGiftAmountPaid ?? 0;
  const formattedCashGiftAmount = formatPrice(
    cashGiftAmountPaid,
    guestDetails?.eventDetails?.preferredCurrency,
  );

  const ticketAmountPaid = paymentDetails?.ticketFeePaid ?? 0;
  const formattedTicketAmount = formatPrice(
    ticketAmountPaid,
    guestDetails?.eventDetails?.preferredCurrency,
  );

  const deliveryAddress = paymentDetails?.deliveryAddress;

  const userData = isGuestViewing
    ? {
        fullName: `${guestDetails.hostDetails?.hostFirstName || ""} ${
          guestDetails.hostDetails?.hostLastName || ""
        }`,
        email: guestDetails.hostDetails?.hostEmail,
        phoneNumber: guestDetails.hostDetails?.hostPhoneNumber,
        userPhoto: guestDetails.hostDetails?.hostProfileImage,
        label: "Host",
      }
    : {
        fullName: `${guestDetails.guestDetails?.firstName || ""} ${
          guestDetails.guestDetails?.lastName || ""
        }`,
        email: guestDetails.guestDetails?.email,
        phoneNumber: guestDetails.guestDetails?.phoneNumber,
        userPhoto: guestDetails.guestDetails?.profileImage,
        label: "Guest",
      };

  const guest = {
    fullName: `${guestDetails.guestDetails?.firstName} ${guestDetails.guestDetails?.lastName}`,
    email: guestDetails.guestDetails?.email,
    phoneNumber: guestDetails.guestDetails?.phoneNumber,

    cashGift: formatPrice(guestDetails.eventDetails?.cashGiftAmount || "₦0"),
    ticket: guestDetails.eventDetails?.ticketType || "Nil",
    eventAttire: guestDetails.eventDetails?.eventAttire?.[0]?.attireTag,
    userPhoto: guestDetails.guestDetails?.profileImage,
    deliveryAddress: guestDetails.eventDetails?.deliveryAddress,
    bankName: guestDetails.refundDetails?.bankDetails?.bankName,
    accountNumber: guestDetails.refundDetails?.bankDetails?.accountNumber,
    accountName: guestDetails.refundDetails?.bankDetails?.accountName,
    eventAttirePrice: formatPrice(
      totalRefundAmount,
      guestDetails.refundDetails?.currency,
    ),
    event: guestDetails.eventDetails?.eventTitle,
    date: formatDateLessThanOneDay(
      guestDetails.eventDetails?.eventAttire?.refundDeadlineDate,
    ),
    status: guestDetails.refundDetails?.refundStatus,
    attireQuantity: refundQuantity,
    giftDescription: cashGiftDescription,
    ticketPrice: formattedTicketAmount,
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!isOpen) return null;

  const isPending = refundStatus === "pending";

  const isRefundDeadlineActive = guestDetails.eventDetails?.eventAttire
    ?.refundDeadlineDate
    ? new Date(guestDetails.eventDetails.eventAttire.refundDeadlineDate) >
      new Date()
    : false;

  const isGuestPendingRefund = isGuestViewing && isPending && !isHistoryTab;
  const hostHistoryTab = isHostViewing && isHistoryTab;

  const canShowRefundActions = !isUsher && !hideRefundActions && isPending;
  const isRefundView = isHistoryTab || refundStatus;
  const canShowBankDetails = isHostViewing || isGuestViewing;

  const handleDeclineClick = () => {
    setModalType("declineConfirmation");
    setIsModalOpen(true);
  };

  const handleApproveClick = () => {
    setModalType("approveConfirmation");
    setIsModalOpen(true);
  };

  const handleProceedDecline = async () => {
    try {
      setActionLoading(true);
      await onDecline();
      setModalType("declined");
    } catch (err) {
      setModalType("failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRefund = async () => {
    setModalType("cancelRefundConfirmation");
    setIsModalOpen(true);
  };

  const handleProceed = async () => {
    try {
      setActionLoading(true);

      if (modalType === "declineConfirmation") {
        await onDecline();
        setModalType("declined");
      }

      if (modalType === "approveConfirmation") {
        await onApprove();
        setModalType("approved");
      }
      if (modalType === "cancelRefundConfirmation") {
        await onCancelRefund();
        setModalType("cancelled");
      }
    } catch (err) {
      if (modalType === "cancelRefundConfirmation") {
        setModalType("failedCancel");
      } else if (modalType === "approveConfirmation") {
        setModalType("failedApproval");
      } else {
        setModalType("failed");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = () => {
    setIsModalOpen(false);
    onClose();
  };

  const handleTryAgain = () => {
    if (modalType === "failed") {
      setModalType("declineConfirmation");
    }

    if (modalType === "failedApproval") {
      setModalType("approveConfirmation");
    }
  };

  const getRefundStatusUI = (status) => {
    switch (status) {
      case "accepted":
        return { label: "Approved", className: "approved" };
      case "declined":
        return { label: "Rejected", className: "rejected" };
      case "cancelled":
        return { label: "Cancelled", className: "cancelled" };
      case "pending":
        return { label: "Pending", className: "pending" };
      default:
        return { label: "Pending", className: "pending" };
    }
  };
  const refundStatusUI = getRefundStatusUI(refundStatus);

  const renderRefundItems = () => {
    if (!refundItemsData.length) return null;

    return (
      <div className="attire-refund-wrapper">
        {canShowRefundActions && (
          <div className="refund-status-row">
            <label className="refund-status-label">Refund status:</label>

            <span className={`refund-status badge ${refundStatusUI.className}`}>
              {refundStatusUI.label}
            </span>
          </div>
        )}

        {refundItemsData.map((item, index) => (
          <div
            key={index}
            className={`attire-section ${refundStatusUI.className}`}
          >
            <div className="attire-image-container">
              <img
                src={item.uri || "/images/eventImage2.jpg"}
                alt={item.attireTag}
                className="attire-image"
              />
            </div>

            <div className="attire-details">
              <h4>{item.attireTag}</h4>

              <p className="unit-price">
                @ <strong>{item.unitPriceFormatted}</strong>
              </p>

              <p className="attire-quantity">
                Quantity to refund: <strong>{item.quantity}</strong>
              </p>
              {!canShowRefundActions && deliveryAddress && (
                <p className="attire-quantity">
                  Delivery Address:{" "}
                  <strong title={deliveryAddress}>
                    {formatAddress(deliveryAddress)}
                  </strong>
                </p>
              )}

              <div className="attire-price-display">
                <label>Item total</label>
                <span>{item.totalPriceFormatted}</span>
              </div>
            </div>
          </div>
        ))}

        <div className={`refund-summary ${refundStatusUI.className}`}>
          <div className="refund-summary-left">
            <label>Total refund</label>
          </div>

          <div className="refund-total-amount">{totalRefundFormatted}</div>
        </div>
      </div>
    );
  };

  const renderPaymentDetails = () => {
    return (
      <div className="attire-refund-wrapper">
        {paymentItemsData.map((item, index) => (
          <div
            key={index}
            className={`attire-section ${refundStatusUI.className}`}
          >
            <div className="attire-image-container">
              <img
                src={item.uri || "/images/eventImage2.jpg"}
                alt={item.attireTag}
                className="attire-image"
              />
            </div>

            <div className="attire-details">
              <h4>{item.attireTag}</h4>

              <p className="unit-price">
                @ <strong>{item.unitPriceFormatted}</strong>
              </p>

              <p className="attire-quantity">
                Quantity: <strong>{item.quantity}</strong>
              </p>
              {deliveryAddress && (
                <p className="attire-quantity">
                  Delivery Address:{" "}
                  <strong title={deliveryAddress}>
                    {formatAddress(deliveryAddress)}
                  </strong>
                </p>
              )}

              <div className="attire-price-display">
                <label>Item total</label>
                <span>{item.totalPriceFormatted}</span>
              </div>
            </div>
          </div>
        ))}

        {activeRole === "host" && !canShowRefundActions && hostHistoryTab && (
          <div className={`refund-summary ${refundStatusUI.className}`}>
            <div className="refund-summary-left">
              <label>Total Attire Amount</label>
            </div>

            <div className="refund-total-amount">{totalPaymentFormatted}</div>
          </div>
        )}
      </div>
    );
  };

  const IconBubble = ({ children }) => (
    <div className="attire-image cash-gift-icon-bubble">{children}</div>
  );

  return (
    <>
      <div className="guest-modal-overlay">
        <div className="guest-modal-container">
          <div className="guest-modal-header">
            <h2>{isRefundView ? "Refund Details" : "Guest Details"}</h2>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="guest-modal-content">
            <div className="guest-event-header">
              <div className={isMobile ? "event-title mobile" : "event-title"}>
                <h3>{guest.event}</h3>

                {isRefundView && (
                  <span className="event-date">
                    <label>Refund Deadline Date</label>
                    {guest.date}
                  </span>
                )}
              </div>

              <div className="guest-profile">
                <div className="avatar">
                  <img
                    src={userData.userPhoto || "/images/avatar.webp"}
                    alt={userData.fullName}
                    className="guest-avatar"
                    onError={(e) => {
                      e.target.src = "/images/avatar.webp";
                    }}
                  />
                </div>
                <div className="guest-name-section">
                  <label>{userData.label} Name</label>
                  <h4>{userData.fullName}</h4>
                </div>
                {showRSVP && (
                  <div className="rsvp-container">
                    <button
                      className={`rsvp-button ${
                        guest.status === "accepted" ? "accepted" : "pending"
                      }`}
                      disabled
                    >
                      <span className="crown-icon">
                        {guest.status === "accepted"
                          ? "✅"
                          : guest.status === "declined"
                            ? "❌"
                            : "⏳"}
                      </span>
                      {guest.status}
                    </button>
                  </div>
                )}
              </div>

              {isUsher && (
                <div className="usher-checkin-info">
                  <div className="info-group">
                    <label>Check-In Status</label>
                    <p
                      className={`checkin-status-text ${
                        guest.isCheckedIn ? "checked-in" : "not-checked-in"
                      }`}
                    >
                      {guest.isCheckedIn
                        ? "✅ Checked In"
                        : "❌ Not Checked In"}
                      {guest.isCheckedIn && guest.checkInTime && (
                        <span className="checkin-time">
                          {" "}
                          at {new Date(guest.checkInTime).toLocaleString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {!isUsher && (
                <>
                  {isMobile ? (
                    <>
                      <div className="info-group">
                        <label>Email</label>
                        <p>{userData.email}</p>
                      </div>
                      <div className="info-group">
                        <label>Phone Number</label>
                        <p>{userData.phoneNumber}</p>
                      </div>
                      {renderRefundItems()}
                      {renderPaymentDetails()}
                    </>
                  ) : (
                    <>
                      <div className="guest-details-contact-info">
                        <div className="info-row">
                          <div className="info-left">
                            <label>Email</label>
                            <p className="ellipsis-text">{userData.email}</p>
                          </div>
                          <div className="info-right">
                            <label>Phone Number</label>
                            <p>{userData.phoneNumber}</p>
                          </div>
                        </div>
                      </div>

                      {renderRefundItems()}
                      {renderPaymentDetails()}
                    </>
                  )}

                  {activeRole === "host" &&
                    !canShowRefundActions &&
                    cashGiftAmountPaid > 0 && (
                      <div className="attire-section gift">
                        <div className="attire-image-container">
                          <IconBubble>🎁</IconBubble>
                        </div>

                        <div className="attire-details">
                          <h4>Cash Gift</h4>

                          <div className="attire-price-display">
                            <label>Amount paid</label>
                            <span>{formattedCashGiftAmount}</span>
                          </div>

                          {cashGiftDescription && (
                            <p className="gift-description-text">
                              {cashGiftDescription}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                  {activeRole === "host" &&
                    !canShowRefundActions &&
                    (ticketAmountPaid > 0 || paymentDetails.ticketType) && (
                      <div className="attire-section ticket">
                        <div className="attire-image-container">
                          <Ticket size={80} color="#FFA500" strokeWidth={1.5} />
                        </div>

                        <div className="attire-details">
                          <h4>Ticket</h4>

                          <p className="unit-price">
                            Type: <strong>{paymentDetails.ticketType}</strong>
                          </p>

                          <div className="attire-price-display">
                            <label>Amount paid</label>
                            <span>
                              {ticketAmountPaid > 0
                                ? formattedTicketAmount
                                : "Free"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                  {canShowBankDetails &&
                    refundItems &&
                    refundStatus === "pending" && (
                      <div className="refund-request-content">
                        <div className="refund-request-content">
                          {(guest.bankName || guest.accountNumber) && (
                            <div className="purchase-bank-details">
                              <label className="bank-section-title">
                                🏦 Refund Bank Details
                              </label>

                              <div className="bank-detail-row">
                                <span className="guest-details-bank-label">
                                  Bank:{" "}
                                </span>
                                <span className="guest-details-bank-value">
                                  {guest.bankName}
                                </span>
                              </div>

                              <div className="bank-detail-row">
                                <span className="guest-details-bank-label">
                                  Account Name:{" "}
                                </span>
                                <span className="guest-details-bank-value">
                                  {guest.accountName}
                                </span>
                              </div>

                              <div className="bank-detail-row">
                                <span className="guest-details-bank-label">
                                  Account Number:{" "}
                                </span>
                                <span className="guest-details-bank-value">
                                  {guest.accountNumber}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>

          {canShowRefundActions && (
            <div className="refund-request-content">
              <div className="guest-refund-actions">
                <button
                  className={`decline-button ${!isRefundDeadlineActive ? "attire-button-disabled" : ""}`}
                  onClick={handleDeclineClick}
                  disabled={!isRefundDeadlineActive}
                >
                  {actionLoading ? "Processing..." : "Decline"}
                </button>
                <button
                  className={`approve-button ${!isRefundDeadlineActive ? "attire-button-disabled" : ""}`}
                  onClick={handleApproveClick}
                  disabled={!isRefundDeadlineActive}
                >
                  {actionLoading ? "Processing..." : "Approve refund"}
                </button>
              </div>
            </div>
          )}

          {isGuestPendingRefund && (
            <div className="refund-request-content">
              <div className="guest-refund-actions">
                <button
                  className={`approve-button ${!isRefundDeadlineActive ? "attire-button-disabled" : ""}`}
                  onClick={handleCancelRefund}
                  disabled={actionLoading || !isRefundDeadlineActive}
                >
                  {actionLoading ? "Cancelling..." : "Cancel Refund Request"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <DeclinedApprovedModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        modalType={modalType}
        onProceed={handleProceed}
        onDismiss={handleDismiss}
        onTryAgain={handleTryAgain}
        actionLoading={actionLoading}
      />
    </>
  );
};

export default GuestDetailsModal;
